import { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Plus, Edit, Trash2, ArrowLeft, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';

interface Seller {
  id: number;
  name: string;
  idNo: string;
  address: string | null;
  contact: string;
  createdAt: string;
  updatedAt: string;
}

const emptyForm = { name: '', idNo: '', address: '', contact: '' };

export default function SellersSettings() {
  const { t } = useLanguage();
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(emptyForm);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [editSeller, setEditSeller] = useState<Seller | null>(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteDeleting, setDeleteDeleting] = useState(false);

  useEffect(() => {
    fetchSellers();
  }, []);

  const fetchSellers = async () => {
    setLoading(true);
    try {
      const data = await api.get('/api/sellers?limit=1000');
      setSellers(data?.sellers ?? (Array.isArray(data) ? data : []));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!addForm.name.trim() || !addForm.idNo.trim() || !addForm.contact.trim()) return;
    setAddSubmitting(true);
    try {
      await api.post('/api/sellers', {
        name: addForm.name.trim(),
        idNo: addForm.idNo.trim(),
        address: addForm.address || null,
        contact: addForm.contact.trim(),
      });
      setAddForm(emptyForm);
      setAddOpen(false);
      fetchSellers();
    } catch (error: unknown) {
      console.error(error);
    } finally {
      setAddSubmitting(false);
    }
  };

  const openEdit = (s: Seller) => {
    setEditSeller(s);
    setEditForm({
      name: s.name,
      idNo: s.idNo,
      address: s.address || '',
      contact: s.contact,
    });
  };

  const handleUpdate = async () => {
    if (!editSeller || !editForm.name.trim() || !editForm.idNo.trim() || !editForm.contact.trim()) return;
    setEditSubmitting(true);
    try {
      await api.put(`/api/sellers/${editSeller.id}`, {
        name: editForm.name.trim(),
        idNo: editForm.idNo.trim(),
        address: editForm.address || null,
        contact: editForm.contact.trim(),
      });
      setEditSeller(null);
      setEditForm(emptyForm);
      fetchSellers();
    } catch (error: unknown) {
      console.error(error);
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId == null) return;
    setDeleteDeleting(true);
    try {
      await api.delete(`/api/sellers/${deleteId}`);
      setDeleteId(null);
      fetchSellers();
    } catch (error: unknown) {
      console.error(error);
    } finally {
      setDeleteDeleting(false);
    }
  };

  const sellerToDelete = deleteId != null ? sellers.find((s) => s.id === deleteId) : null;
  const q = searchQuery.trim().toLowerCase();
  const filteredSellers = sellers.filter(
    (s) =>
      s.name.toLowerCase().includes(q) ||
      s.idNo.toLowerCase().includes(q) ||
      s.contact.toLowerCase().includes(q) ||
      (s.address && s.address.toLowerCase().includes(q))
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/settings">
              <Button variant="outline" size="sm" className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('settings.back')}
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('settings.sellersManagement')}</h1>
              <p className="text-slate-500 text-sm mt-0.5">{t('settings.sellersDescription')}</p>
            </div>
          </div>
          <Button onClick={() => setAddOpen(true)} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="h-4 w-4 mr-2" />
            {t('settings.addSeller')}
          </Button>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardContent className="p-6">
            {sellers.length > 0 && (
              <div className="relative mb-4 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={t('settings.search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 rounded-lg border-slate-200"
                />
              </div>
            )}
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-10 w-full rounded-lg" />
                ))}
              </div>
            ) : sellers.length === 0 ? (
              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-12 text-center">
                <p className="text-slate-500">{t('settings.noSellersYet')}</p>
              </div>
            ) : filteredSellers.length === 0 ? (
              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-12 text-center">
                <p className="text-slate-500">{t('settings.search')} — {t('settings.noSearchResults')}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-slate-600 font-medium text-sm">{t('settings.sellerName')}</TableHead>
                    <TableHead className="text-slate-600 font-medium text-sm">{t('settings.sellerIdNo')}</TableHead>
                    <TableHead className="text-slate-600 font-medium text-sm">{t('settings.sellerContact')}</TableHead>
                    <TableHead className="text-slate-600 font-medium text-sm hidden md:table-cell">{t('settings.sellerAddress')}</TableHead>
                    <TableHead className="text-slate-600 font-medium text-sm hidden lg:table-cell">{t('settings.created')}</TableHead>
                    <TableHead className="w-[120px] text-right text-slate-600 font-medium text-sm">{t('settings.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSellers.map((s) => (
                    <TableRow key={s.id} className="hover:bg-slate-50">
                      <TableCell className="text-sm text-slate-700 font-medium">{s.name}</TableCell>
                      <TableCell className="text-sm text-slate-700 tabular-nums">{s.idNo}</TableCell>
                      <TableCell className="text-sm text-slate-700">{s.contact}</TableCell>
                      <TableCell className="text-sm text-slate-600 hidden md:table-cell max-w-[180px] truncate">{s.address || '—'}</TableCell>
                      <TableCell className="text-sm text-slate-500 tabular-nums hidden lg:table-cell">{new Date(s.createdAt).toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="outline" size="sm" className="border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800" onClick={() => openEdit(s)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => setDeleteId(s.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add Seller Dialog */}
      <Dialog open={addOpen} onOpenChange={(open) => !addSubmitting && setAddOpen(open)}>
        <DialogContent className="sm:max-w-md border-slate-200">
          <DialogHeader>
            <DialogTitle>{t('settings.addSeller')}</DialogTitle>
            <DialogDescription>{t('settings.sellersDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-slate-700">{t('settings.sellerName')} *</Label>
              <Input value={addForm.name} onChange={(e) => setAddForm((p) => ({ ...p, name: e.target.value }))} placeholder={t('settings.sellerName')} className="rounded-lg border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">{t('settings.sellerIdNo')} *</Label>
              <Input value={addForm.idNo} onChange={(e) => setAddForm((p) => ({ ...p, idNo: e.target.value }))} placeholder={t('settings.sellerIdNo')} className="rounded-lg border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">{t('settings.sellerContact')} *</Label>
              <Input value={addForm.contact} onChange={(e) => setAddForm((p) => ({ ...p, contact: e.target.value }))} placeholder={t('settings.sellerContact')} className="rounded-lg border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">{t('settings.sellerAddress')}</Label>
              <Input value={addForm.address} onChange={(e) => setAddForm((p) => ({ ...p, address: e.target.value }))} placeholder={t('settings.sellerAddress')} className="rounded-lg border-slate-200" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setAddOpen(false)} disabled={addSubmitting} className="border-slate-200">{t('settings.cancel')}</Button>
            <Button type="button" onClick={handleCreate} disabled={!addForm.name.trim() || !addForm.idNo.trim() || !addForm.contact.trim() || addSubmitting} className="bg-slate-900 hover:bg-slate-800">{t('settings.addSeller')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Seller Dialog */}
      <Dialog open={editSeller != null} onOpenChange={(open) => !open && !editSubmitting && setEditSeller(null)}>
        <DialogContent className="sm:max-w-md border-slate-200">
          <DialogHeader>
            <DialogTitle>{t('settings.editSeller')}</DialogTitle>
            <DialogDescription>{t('settings.sellersDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-slate-700">{t('settings.sellerName')} *</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} className="rounded-lg border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">{t('settings.sellerIdNo')} *</Label>
              <Input value={editForm.idNo} onChange={(e) => setEditForm((p) => ({ ...p, idNo: e.target.value }))} className="rounded-lg border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">{t('settings.sellerContact')} *</Label>
              <Input value={editForm.contact} onChange={(e) => setEditForm((p) => ({ ...p, contact: e.target.value }))} className="rounded-lg border-slate-200" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">{t('settings.sellerAddress')}</Label>
              <Input value={editForm.address} onChange={(e) => setEditForm((p) => ({ ...p, address: e.target.value }))} className="rounded-lg border-slate-200" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setEditSeller(null)} disabled={editSubmitting} className="border-slate-200">{t('settings.cancel')}</Button>
            <Button type="button" onClick={handleUpdate} disabled={!editForm.name.trim() || !editForm.idNo.trim() || !editForm.contact.trim() || editSubmitting} className="bg-slate-900 hover:bg-slate-800">{t('settings.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Seller Dialog */}
      <Dialog open={deleteId != null} onOpenChange={(open) => !open && !deleteDeleting && setDeleteId(null)}>
        <DialogContent className="sm:max-w-md border-slate-200">
          <DialogHeader>
            <DialogTitle>{t('settings.deleteSeller')}</DialogTitle>
            <DialogDescription>{sellerToDelete ? `"${sellerToDelete.name}" ${t('settings.confirmDelete')}` : t('settings.confirmDelete')}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setDeleteId(null)} disabled={deleteDeleting} className="border-slate-200">{t('settings.cancel')}</Button>
            <Button type="button" variant="destructive" disabled={deleteDeleting} className="bg-red-600 hover:bg-red-700" onClick={handleDelete}>{deleteDeleting ? t('settings.deleting') : t('settings.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
